import { isSamePlatformId } from '@app/modules/social-export/domain/is-same-platform-id';
import { SocialPlatform, socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { useAvailableSocialExportPlatformsForStation } from '@app/modules/social-export/hooks/useAvailableSocialExportPlatformsForStation';
import { useStationConnections } from '@app/modules/social-export/hooks/useStationConnections';
import orderBy from 'lodash/orderBy';
import { useMemo } from 'react';

export interface LastUsedPlatformForStationResult {
  /** Preferred platform for the station, or undefined while connections are loading / none exist. */
  platform: SocialPlatform | undefined;
  areConnectionsResolved: boolean;
}

/**
 * Resolves the platform whose connection was most recently used for this station.
 * Falls back to the first available platform alphabetically when nothing has been used yet.
 */
export function useLastUsedPlatformForStation(
  stationId: string | undefined,
): LastUsedPlatformForStationResult {
  const { connections, areConnectionsResolved } = useStationConnections(stationId);
  const { platforms: availablePlatforms } = useAvailableSocialExportPlatformsForStation(stationId);

  const platform = useMemo(() => {
    if (!areConnectionsResolved || availablePlatforms.length === 0) {
      return undefined;
    }

    const mostRecent = orderBy(connections, [(c) => c.lastUsedForStationAt ?? ''], ['desc'])[0];

    if (mostRecent) {
      const matched = availablePlatforms.find((p) => isSamePlatformId(p.id, mostRecent.platform));
      if (matched) {
        return matched;
      }
    }

    return availablePlatforms[0] ?? socialPlatforms.all()[0];
  }, [areConnectionsResolved, availablePlatforms, connections]);

  return { platform, areConnectionsResolved };
}
